import {
  Badge,
  Banner,
  Button,
  EmptyState,
  FlexBox,
  Page,
  SimplePagination,
  Spinner,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import type { Schedule, ScheduleListItem } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BUSINESS_HOURS_MAX_NUMBER_SCHEDULES } from 'app/app.constants';
import { useTableHistory } from 'hooks/useTableHistory/useTableHistory';

import { AddModal } from './components/addModal/addModal';
import { DeleteModal } from './components/deleteModal/deleteModal';
import { DuplicateModal } from './components/duplicateModal/duplicateModal';
import { FirstScheduleModal } from './components/firstScheduleModal/firstScheduleModal';
import { RenameModal } from './components/renameModal/renameModal';
import { TableRow } from './components/tableRow/tableRow';
import {
  OpeningHoursContext,
  OpeningHoursProvider,
} from './openingHours.context';
import styles from './openingHours.view.module.scss';

export type ScheduleListPageState = {
  editedSchedule: Pick<Schedule, 'id' | 'name'>;
  isFirstScheduleCreated: boolean;
} | null;

export type ScheduleModalType =
  | 'create'
  | 'delete'
  | 'duplicate'
  | 'first-schedule'
  | 'rename';

export type ScheduleModalProps = {
  schedule: ScheduleListItem;
  onClose: VoidFunction;
  refetchItems: VoidFunction;
};

const tableColumns = ['Name', 'Timezone', 'Used in'];

export const OpeningHoursView = (): JSX.Element => {
  const location = useLocation();
  const { initiallyExpandedRows } = useTableHistory();
  const [activeSchedule, setActiveSchedule] = useState<null | ScheduleListItem>(
    null,
  );
  const [activeModalType, setActiveModalType] =
    useState<null | ScheduleModalType>(() =>
      (location.state as ScheduleListPageState)?.isFirstScheduleCreated
        ? 'first-schedule'
        : null,
    );

  const handleModalOpen = useCallback(
    (
        modalType: null | ScheduleModalType,
        schedule: null | ScheduleListItem = null,
      ) =>
      (): void => {
        setActiveModalType(modalType);
        setActiveSchedule(schedule);
      },
    [],
  );

  const [Schedule, Timezone, AppliedTo] = tableColumns;

  return (
    <OpeningHoursProvider>
      <OpeningHoursContext.Consumer>
        {({
          hasNextPage,
          hasPreviousPage,
          isLoading,
          schedules,
          schedulesInConflicts,
          totalNumberOfSchedules,
          nextPage,
          previousPage,
          refetchItems,
          refetchSchedulesInConflicts,
        }) => {
          const hasMaxNumberOfSchedulesReached =
            totalNumberOfSchedules >= BUSINESS_HOURS_MAX_NUMBER_SCHEDULES;
          const hasConflicts = !!schedulesInConflicts.length;

          return (
            <>
              <Page.Section
                title="All schedules"
                titleMeta={
                  <Badge size="small" variant="secondary">
                    {totalNumberOfSchedules}/
                    {BUSINESS_HOURS_MAX_NUMBER_SCHEDULES}
                  </Badge>
                }
                controls={
                  <>
                    <Tooltip
                      content="You need to delete a schedule before you can add a new one"
                      disabled={!hasMaxNumberOfSchedulesReached}
                      side="left"
                      wrap="div"
                    >
                      <Button
                        disabled={hasMaxNumberOfSchedulesReached}
                        onClick={() => {
                          handleModalOpen('create')();
                          ampli.businessHoursAddScheduleButtonClicked();
                        }}
                      >
                        Add new schedule
                      </Button>
                    </Tooltip>
                  </>
                }
                callout={
                  (hasConflicts || hasMaxNumberOfSchedulesReached) && (
                    <FlexBox flexDirection="column" gap="300">
                      {hasConflicts && (
                        <Banner
                          variant="warning"
                          headline="Please review conflicting dates"
                          description={
                            <Text.Paragraph variant="4">
                              We found date conflicts in{' '}
                              <strong>
                                {schedulesInConflicts.length}{' '}
                                {schedulesInConflicts.length === 1
                                  ? 'schedule'
                                  : 'schedules'}
                              </strong>
                              . For each schedule, please select which business
                              hours apply on the conflicting dates.
                            </Text.Paragraph>
                          }
                        />
                      )}

                      {hasMaxNumberOfSchedulesReached && (
                        <Banner
                          description={
                            <Text.Paragraph
                              color={colors.neutral[800]}
                              variant="4"
                            >
                              You have reached the maximum number of schedules.
                            </Text.Paragraph>
                          }
                          variant="info"
                        />
                      )}
                    </FlexBox>
                  )
                }
              >
                <Table
                  expandableRowsProps={{
                    expandableRows: true,
                    initiallyExpandedRows,
                  }}
                  stickyHeader
                  stickyFooter
                >
                  <Table.Thead>
                    <Table.Tr>
                      {schedules.length ? (
                        <>
                          <Table.Th width="40%">{Schedule}</Table.Th>
                          <Table.Th width="30%">{Timezone}</Table.Th>
                          <Table.Th width="30%">{AppliedTo}</Table.Th>
                          <Table.Th />
                        </>
                      ) : (
                        <Table.Th></Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {schedules.length ? (
                      schedules.map((schedule) => (
                        <TableRow
                          key={schedule.id}
                          schedule={schedule}
                          onModalOpen={handleModalOpen}
                        />
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={tableColumns.length}>
                          {isLoading ? (
                            <Spinner className={styles.spinner} />
                          ) : (
                            <EmptyState
                              description="You haven’t created any schedules yet. Click on the purple button to get started!"
                              icon={null}
                              title="Nothing to show"
                            />
                          )}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>

                  <Table.Tfoot className={styles.tableFoot}>
                    <Table.Tr>
                      <Table.Td colSpan={tableColumns.length + 2}>
                        <SimplePagination
                          previousButtonClick={previousPage}
                          nextButtonClick={nextPage}
                          previousButtonDisabled={!hasPreviousPage}
                          nextButtonDisabled={!hasNextPage}
                          loading={isLoading}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </Page.Section>

              {/* Modals */}
              {activeModalType === 'first-schedule' && (
                <FirstScheduleModal onClose={handleModalOpen(null)} />
              )}
              {activeModalType === 'create' && (
                <AddModal onClose={handleModalOpen(null)} />
              )}
              {activeModalType === 'rename' && activeSchedule && (
                <RenameModal
                  mode="update"
                  onClose={handleModalOpen(null)}
                  refetchItems={refetchItems}
                  schedule={activeSchedule}
                />
              )}
              {activeModalType === 'duplicate' && activeSchedule && (
                <DuplicateModal
                  onClose={handleModalOpen(null)}
                  schedule={activeSchedule}
                />
              )}
              {activeModalType === 'delete' && activeSchedule && (
                <DeleteModal
                  onClose={handleModalOpen(null)}
                  refetchItems={() => {
                    refetchItems();
                    refetchSchedulesInConflicts();
                  }}
                  schedule={activeSchedule}
                />
              )}
            </>
          );
        }}
      </OpeningHoursContext.Consumer>
    </OpeningHoursProvider>
  );
};
